"use client";

import { AlertCircle, ArrowRight, CreditCard, ShieldAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { useGroupSubscription } from "../hooks/api/subscription/use-group-subscription";
import { useGetCurrentUser } from "../hooks/api/users/use-current-user";

type PaymentAlertStatus = "PAST_DUE" | "SUSPENDED";

interface AlertConfig {
  title: string;
  message: string;
  ctaLabel: string;
  ctaAriaLabel: string;
  containerClass: string;
  iconWrapClass: string;
  iconClass: string;
  titleClass: string;
  messageClass: string;
  linkClass: string;
  Icon: typeof AlertCircle;
}

const ALERT_CONFIG: Record<PaymentAlertStatus, AlertConfig> = {
  PAST_DUE: {
    title: "Paiement en attente",
    message:
      "Le prélèvement de votre abonnement a échoué. Mettez à jour votre carte pour éviter la suspension de votre accès.",
    ctaLabel: "Mettre à jour",
    ctaAriaLabel: "Mettre à jour les informations de paiement",
    containerClass:
      "border-b border-amber-300/60 bg-gradient-to-r from-amber-50 to-orange-50/80 dark:border-amber-700/40 dark:from-amber-950/50 dark:to-orange-950/30",
    iconWrapClass:
      "bg-amber-100 ring-amber-300/60 dark:bg-amber-900/60 dark:ring-amber-700/40",
    iconClass: "text-amber-600 dark:text-amber-400",
    titleClass: "text-amber-900 dark:text-amber-200",
    messageClass: "text-amber-800/90 dark:text-amber-300/90",
    linkClass:
      "border-amber-400/60 bg-amber-100 text-amber-900 hover:border-amber-500 hover:bg-amber-200 dark:border-amber-700/60 dark:bg-amber-900/50 dark:text-amber-200 dark:hover:bg-amber-900/80",
    Icon: CreditCard,
  },
  SUSPENDED: {
    title: "Accès suspendu",
    message:
      "Votre compte est suspendu suite à un impayé. Régularisez votre paiement pour retrouver l'accès à vos données.",
    ctaLabel: "Débloquer mon compte",
    ctaAriaLabel: "Débloquer mon compte et régulariser le paiement",
    containerClass:
      "border-b border-red-300/60 bg-gradient-to-r from-red-50 to-rose-50/80 dark:border-red-800/40 dark:from-red-950/50 dark:to-rose-950/30",
    iconWrapClass: "bg-red-100 ring-red-300/60 dark:bg-red-900/60 dark:ring-red-800/40",
    iconClass: "text-red-600 dark:text-red-400",
    titleClass: "text-red-900 dark:text-red-200",
    messageClass: "text-red-800/90 dark:text-red-300/90",
    linkClass:
      "border-red-400/60 bg-red-100 text-red-900 hover:border-red-500 hover:bg-red-200 dark:border-red-800/60 dark:bg-red-900/50 dark:text-red-200 dark:hover:bg-red-900/80",
    Icon: ShieldAlert,
  },
};

export function SubscriptionAlertBanner() {
  const { data: user, isLoading: isUserLoading } = useGetCurrentUser();
  const groupeId = user?.data?.groupe?.id as number | undefined;
  const { data: subscription, isLoading: isSubscriptionLoading } = useGroupSubscription(groupeId);

  if (isUserLoading || isSubscriptionLoading || !subscription || !groupeId) return null;
  if (subscription.plan_type === "FREE") return null;

  const status = subscription.subscription_status as string;
  if (status !== "PAST_DUE" && status !== "SUSPENDED") return null;

  const config = ALERT_CONFIG[status as PaymentAlertStatus];

  return (
    <div
      role="alert"
      aria-live="polite"
      className={`flex items-center justify-between gap-3 px-4 py-3 ${config.containerClass}`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-1 ${config.iconWrapClass}`}
        >
          <config.Icon className={`h-4 w-4 ${config.iconClass}`} aria-hidden="true" />
        </div>
        <div className="min-w-0 space-y-0.5">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <AlertCircle className={`h-3.5 w-3.5 shrink-0 ${config.iconClass}`} aria-hidden="true" />
            <span className={`text-sm font-semibold ${config.titleClass}`}>{config.title}</span>
          </div>
          <p className={`text-xs leading-relaxed ${config.messageClass}`}>{config.message}</p>
        </div>
      </div>

      <Link
        to="/admin/subscription"
        className={`flex shrink-0 items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${config.linkClass}`}
        aria-label={config.ctaAriaLabel}
      >
        {config.ctaLabel}
        <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
      </Link>
    </div>
  );
}
